import { css } from 'styled-components';

import Sizes from '../definitions/Sizes';
import ZIndex from '../definitions/ZIndex';
import Colours from '../definitions/Colours';
import Typography from '../definitions/Typography';


/**
 * Provides CSS properties to position a light lime background ribbon in the top-left corner of a container.
 * Typically used when adding a ribbon message for "sponsored" or "featured" messages.
 *
 * @example
 * The message itself is provided by the caller via a `data-ribbon-message={ribbonString}` attribute on the container
 * and CSS of `::before { content: attr(data-ribbon-message) }`.
 *
 * @param {string} [offsetTop='0'] top offset as a CSS string-based measurement (e.g. '5px', '-1.5em', etc.)
 * @param {string} [offsetLeft='-1px'] left offset (by default, we assume a 1px left border)
 * @param {boolean} [shouldLowerCaseMessage=true]
 * @returns {import('styled-components').css}
 */
function createRibbonMixin(
    offsetTop = '0',
    offsetLeft = '-1px',
    shouldLowerCaseMessage = true,
) {
    return css`
        position: relative;

        /* Label */
        &::before {
            display: block;
            font-family: ${Typography.FONT_FAMILY_STACK};
            z-index: ${ZIndex.ELEMENTS};

            position: absolute;
            top: ${offsetTop};
            left: calc(-${TRIANGLE_SIZE_PIXELS} + ${offsetLeft});

            height: ${RIBBON_HEIGHT};
            line-height: ${RIBBON_HEIGHT};

            margin: 0;
            padding: 0 ${Sizes.SPACING.THREE_QUARTERS};

            font-size: ${Sizes.FONTS['2XS']};
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            ${shouldLowerCaseMessage ? 'text-transform: lowercase' : undefined};

            background-color: ${Colours.LIME_LIGHT};
            color: ${Colours.BLACKBERRY};
        }

        /* CSS Triangle */
        &::after {
            content: "";
            display: block;

            position: absolute;
            top: calc(${offsetTop} + ${RIBBON_HEIGHT});
            left: calc(-${TRIANGLE_SIZE_PIXELS} + ${offsetLeft});

            width: 0;
            height: 0;

            border-width: 0;
            border-right-width: ${TRIANGLE_SIZE_PIXELS};
            border-bottom-width: ${TRIANGLE_SIZE_PIXELS};
            border-style: solid;
            border-color: ${Colours.TRANSPARENT};
            border-right-color: ${Colours.LIME};
        }
    `;
}

const RIBBON_HEIGHT = Sizes.SPACING.ONE_AND_A_HALF;
const TRIANGLE_SIZE_PIXELS = '10px';


export default createRibbonMixin;
